import React, { useEffect, useState } from 'react';
import { api } from '../../services/api';
import styles from './UnpaidChecks.module.css';

/**
 * "צ'קים שטרם נפרעו" — oversight for checks recorded in PonimDB (CurrentReport)
 * that have not yet cleared at the bank. A check is "not cleared" when it has no
 * redemption date (CheckDueDate) AND no matching bank activity by cheque number.
 *
 * Two tabs:
 *  - "צ'קים": single uncleared checks, issued in the last 6 months, aged from the
 *    issue date: up to 2 months = תקין (green); 2–6 months = מטריד (orange, first);
 *    over 6 months = not shown.
 *  - "פונים לבדיקה": appliers who, over the last 12 months, had checks but NONE
 *    cleared (0% clearance, >= 2 checks) — a systematic red flag.
 */
export default function UnpaidChecksPage({ user }) {
  const [tab, setTab] = useState('checks'); // 'checks' | 'appliers'

  const [data, setData] = useState(null); // { summary, checks }
  const [appliers, setAppliers] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);
  const [showOkToo, setShowOkToo] = useState(true);

  useEffect(() => {
    Promise.all([
      api.getUnpaidChecks(user.credential),
      api.getAppliersUnpaidChecks(user.credential),
    ])
      .then(([checksData, appliersData]) => {
        setData(checksData);
        setAppliers(appliersData);
      })
      .catch(() => setError(true))
      .finally(() => setLoading(false));
  }, [user.credential]);

  const formatAmount = (n) => `₪${Number(n).toLocaleString('he-IL', { minimumFractionDigits: 0 })}`;
  const formatDate = (iso) => iso ? new Date(iso).toLocaleDateString('he-IL') : '—';
  const ageText = (days) => {
    if (days < 30) return `${days} ימים`;
    const months = Math.floor(days / 30);
    return months === 1 ? 'חודש' : `${months} חודשים`;
  };

  const summary = data?.summary;
  const allChecks = data?.checks || [];
  const checks = showOkToo ? allChecks : allChecks.filter(c => c.ageBucket === 'Concerning');
  const appliersList = appliers || [];

  return (
    <div className={styles.container}>
      <h2>צ'קים שטרם נפרעו</h2>
      <p className={styles.sub}>מעקב אחר צ'קים שנרשמו וטרם נפרעו בבנק</p>

      {loading && <p className={styles.empty}>טוען...</p>}
      {error && <p className={styles.error}>שגיאה בטעינת הנתונים</p>}

      {!loading && !error && (
        <>
          {/* Tabs */}
          <div className={styles.tabs}>
            <button
              className={`${styles.tab} ${tab === 'checks' ? styles.tabActive : ''}`}
              onClick={() => setTab('checks')}
            >
              צ'קים {summary ? `(${summary.total})` : ''}
            </button>
            <button
              className={`${styles.tab} ${tab === 'appliers' ? styles.tabActive : ''}`}
              onClick={() => setTab('appliers')}
            >
              פונים לבדיקה {`(${appliersList.length})`}
            </button>
          </div>

          {/* ---- Tab: single checks ---- */}
          {tab === 'checks' && summary && (
            <>
              <div className={styles.summaryGrid}>
                <div className={styles.statCard}>
                  <div className={styles.statValue}>{summary.total}</div>
                  <div className={styles.statLabel}>סה"כ טרם נפרעו</div>
                  <div className={styles.statAmount}>{formatAmount(summary.totalAmount)}</div>
                </div>
                <div className={`${styles.statCard} ${styles.concerningCard}`}>
                  <div className={styles.statValue}>{summary.concerning}</div>
                  <div className={styles.statLabel}>⚠️ מטרידים (מעל חודשיים)</div>
                  <div className={styles.statAmount}>{formatAmount(summary.concerningAmount)}</div>
                </div>
                <div className={`${styles.statCard} ${styles.okCard}`}>
                  <div className={styles.statValue}>{summary.ok}</div>
                  <div className={styles.statLabel}>תקינים (עד חודשיים)</div>
                </div>
              </div>

              <div className={styles.toggleRow}>
                <label className={styles.toggle}>
                  <input
                    type="checkbox"
                    checked={showOkToo}
                    onChange={(e) => setShowOkToo(e.target.checked)}
                  />
                  הצג גם צ'קים תקינים (עד חודשיים)
                </label>
              </div>

              {checks.length === 0 && (
                <p className={styles.empty}>
                  {allChecks.length === 0 ? 'אין צ\'קים שטרם נפרעו 🎉' : 'אין צ\'קים מטרידים כרגע 🎉'}
                </p>
              )}

              <div className={styles.list}>
                {checks.map((c) => {
                  const concerning = c.ageBucket === 'Concerning';
                  return (
                    <div
                      key={c.id}
                      className={`${styles.card} ${concerning ? styles.cardConcerning : styles.cardOk}`}
                    >
                      <div className={styles.cardMain}>
                        <div className={styles.applier}>{c.applierName || '—'}</div>
                        <div className={styles.amount}>{formatAmount(c.amount)}</div>
                      </div>
                      <div className={styles.cardMeta}>
                        <span className={styles.checkNum}>צ'ק #{c.checkNum ?? '—'}</span>
                        <span> · נכתב {formatDate(c.issueDate)}</span>
                        <span
                          className={`${styles.ageBadge} ${concerning ? styles.ageConcerning : styles.ageOk}`}
                        >
                          {concerning ? '⚠️ ' : ''}עברו {ageText(c.ageDays)}
                        </span>
                      </div>
                      {c.payTo && <div className={styles.payTo}>לפקודת: {c.payTo}</div>}
                      {c.checkNote && <div className={styles.note}>{c.checkNote}</div>}
                    </div>
                  );
                })}
              </div>
            </>
          )}

          {/* ---- Tab: appliers to review ---- */}
          {tab === 'appliers' && (
            <>
              <p className={styles.tabHint}>
                פונים שבשנה האחרונה נרשמו להם צ'קים — ואף אחד מהם לא נפרע בבנק. שווה בדיקה.
              </p>

              {appliersList.length === 0 && (
                <p className={styles.empty}>אין פונים כאלה כרגע 🎉</p>
              )}

              <div className={styles.list}>
                {appliersList.map((a) => (
                  <div key={a.applierId} className={`${styles.card} ${styles.cardConcerning}`}>
                    <div className={styles.cardMain}>
                      <div className={styles.applier}>{a.applierName || '—'}</div>
                      <div className={styles.amount}>{formatAmount(a.totalAmount)}</div>
                    </div>
                    <div className={styles.cardMeta}>
                      <span className={`${styles.ageBadge} ${styles.ageConcerning}`}>
                        ⚠️ {a.checkCount} צ'קים — אף אחד לא נפרע
                      </span>
                    </div>
                    <div className={styles.payTo}>
                      טווח: {formatDate(a.firstCheckDate)} – {formatDate(a.lastCheckDate)}
                    </div>
                  </div>
                ))}
              </div>
            </>
          )}
        </>
      )}
    </div>
  );
}
